import React, { useMemo, useState } from 'react';
import { ConfigProvider } from 'tea-component';
import { useIntl, useLocation } from 'umi';
import RangePicker from '@/pages/home/components/RangePicker';
import moment, { Moment } from 'moment';
import { formatDayTime, formatWeekTime } from '@home/pages/studio/utils';
import { LANGUAGE_EN } from '@home/_config/config';
import { ShortcutOptionDay, ShortcutOptionWeek } from '@home/pages/studio/home/components/Filter/_config';
import { Granularity, GranularityConfig, ICommonRangePickerProps } from './type';
import dateTimeFormat from '@home/utils/dbDataFormat/dateTimeFormat';
import { PERIOD_MAP } from '../../_config/constant';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';

/**
 * 验证并解析来自 URL 参数或 props 的日期范围
 * @param startDate - 开始日期字符串
 * @param endDate - 结束日期字符串
 * @returns 有效的 moment 范围或 null
 */
const validateDateRange = (startDate?: string, endDate?: string): [Moment, Moment] | null => {
  if (!startDate || !endDate) {
    return null;
  }

  const start = moment(startDate);
  const end = moment(endDate);

  // 检查两个日期是否有效，且结束日期不早于开始日期
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
    return null;
  }

  return [start, end];
};

/**
 * 从 URL 查询参数中获取日期范围
 * @param search - URL 查询字符串
 * @returns 有效的 moment 范围或 null
 */
const getDateRangeFromURL = (search: string): [Moment, Moment] | null => {
  const params = new URLSearchParams(search);
  const startDate = params.get('startDate');
  const endDate = params.get('endDate');
  
  return validateDateRange(startDate || undefined, endDate || undefined);
};

const granularityConfigs: Record<Granularity, GranularityConfig> = {
  date: {
    getDefaultTimeValue: (timeZone: number) => [
      moment().utcOffset(timeZone).startOf('day').subtract(1, 'days'),
      moment().utcOffset(timeZone).endOf('day').subtract(1, 'days'),
    ],
    formatTime: formatDayTime,
    type: 'daily',
    isSingle: (start, end) => moment(end).diff(start, 'days') === 0,
    startOfWeek: 0,
    shortcutOption: ShortcutOptionDay
  },
  week: {
    getDefaultTimeValue: (timeZone: number) => [
      moment().utcOffset(timeZone).startOf('isoWeek').subtract(1, 'weeks'),
      moment().utcOffset(timeZone).startOf('isoWeek').subtract(1, 'weeks'),
    ],
    formatTime: formatWeekTime,
    type: 'weekly',
    isSingle: (start, end) => moment(end).diff(start, 'days') === 6,
    startOfWeek: 1,
    defualtSelectedShortcut: ShortcutOptionWeek?.week[1],
    shortcutOption: ShortcutOptionWeek
  }
  // to-do: 扩展月粒度
};

export default function CommonRangePicker(props: ICommonRangePickerProps ) {
  const {
    children,
    granularity = 'date',
    timeZone = 0,
    singleWeek = false,
    dateFormatRule = 'YYYY-MM-DD',
    startDate,
    endDate,
    data,
    minDate,
    maxDate,
    ...restProps
  } = props || {};

  const { locale } = useIntl();
  const location = useLocation();
  const isEn = locale === LANGUAGE_EN;
  const config = granularityConfigs[granularity];

  /**
   * 获取初始时间值，优先级：URL > props > 默认值
   */
  const getInitialTimeValue = (): [Moment, Moment] => {
    // 优先级 1: URL 参数
    const urlDateRange = getDateRangeFromURL(location.search);
    if (urlDateRange) {
      return urlDateRange;
    }

    // 优先级 2: 外部 props
    const propDateRange = validateDateRange(startDate, endDate);
    if (propDateRange) {
      return propDateRange;
    }

    // 优先级 3: 默认值
    return config.getDefaultTimeValue(timeZone);
  };

  const [timeValue, setTimeValue] = useState<[Moment, Moment]>(getInitialTimeValue());

  // 筛选器变化
  const [isChange, setIsChange] = useState(false);

  const filter = useMemo(() => {
    const timeVal = config.formatTime(timeValue);
    return {
      start_date: dateTimeFormat( timeVal[0], dateFormatRule ),
      end_date: dateTimeFormat(timeVal[1], dateFormatRule ),
      date_type: config.type,
      isSingle: config.isSingle(timeValue[0], timeValue[1]),
      isChange,
      period: PERIOD_MAP[granularity]
    };
  }, [config, timeValue, dateFormatRule, isChange]);

  return (
    <ConfigProvider locale={isEn ? 'zh' : 'en'}>
      <div className="search-item search-item-date">
        <RangePicker
          type={granularity}
          value={timeValue}
          defualtSelectedShortcut={config?.defualtSelectedShortcut || {}}
          typeList={[granularity]}
          showShortcut={false}
          language={isEn ? 'en' : 'cn'}
          showSelectType={false}
          startOfWeek={config.startOfWeek}
          singleWeek={singleWeek}
          disabledDate={{
            [granularity]: (date: Moment) => {
              const hasMin = minDate != null;
              const hasMax = maxDate != null;
              
              // 同时有 min 和 max，使用 isBetween
              if (hasMin && hasMax) {
                return date.isBetween(moment(minDate).utcOffset(timeZone), moment(maxDate).utcOffset(timeZone), granularity, "[]");
              }
              
              // 只有 min
              if (hasMin) {
                return !date.isBefore(moment(minDate).utcOffset(timeZone), granularity);
              }
              
              // 只有 max
              if (hasMax) {
                return !date.isAfter(moment(maxDate).utcOffset(timeZone),granularity);
              }
              
              // 都没有，不禁用任何日期
              return true;
            },
          }}
          onChange={(value: [Moment, Moment]) => {
            setTimeValue(value);
            setIsChange(true);
          }}
          {...(config?.shortcutOption && { setShortcutOption: () => config?.shortcutOption })}
          {...restProps}
        />
      </div>
      <FilterContext.Provider value={{ filterVal: filter, filterLoading: false, setFilterVal: () => {} }}>
        {React.Children.map(children, child => React.cloneElement(child as React.ReactElement, { data: {...data, filter}, filterLoading: false,...restProps }))}
      </FilterContext.Provider>
    </ConfigProvider>
  );
}
